import {
	subscriptionList,
	subscriptionCreate,
	subscriptionBulkCreate,
	subscriptionUpgrade,
	subscriptionCancel,
} from '@/services/subscriptions.service'

const emptySubscriptionList = () => ({
	data: [],
	meta: {
		pagination: {
			total: 0,
		},
	},
})

const subscriptionListCache = new Map()
const subscriptionListPending = new Map()
let subscriptionListRequestKey = 0

const cacheKey = params => JSON.stringify(params || {})
const clearSubscriptionCache = () => {
	subscriptionListCache.clear()
	subscriptionListPending.clear()
}

export default {
	namespaced: true,
	state: {
		subscriptionList: emptySubscriptionList(),

		subscriptionListStatus: 'init',
		subscriptionCreateStatus: 'init',
		subscriptionBulkCreateStatus: 'init',
		subscriptionUpgradeStatus: 'init',
		subscriptionCancelStatus: 'init',
	},
	getters: {
		getSubscriptionList(state) {
			return state.subscriptionList
		},
		getSubscriptionListStatus(state) {
			return state.subscriptionListStatus
		},
		getSubscriptionCreateStatus(state) {
			return state.subscriptionCreateStatus
		},
		getSubscriptionBulkCreateStatus(state) {
			return state.subscriptionBulkCreateStatus
		},
		getSubscriptionUpgradeStatus(state) {
			return state.subscriptionUpgradeStatus
		},
		getSubscriptionCancelStatus(state) {
			return state.subscriptionCancelStatus
		},
	},
	mutations: {
		SUBSCRIPTION_LIST(state, value) {
			state.subscriptionList = {
				...emptySubscriptionList(),
				...value,
				data: Array.isArray(value?.data) ? value.data : [],
				meta: {
					...emptySubscriptionList().meta,
					...(value?.meta || {}),
					pagination: {
						...emptySubscriptionList().meta.pagination,
						...(value?.meta?.pagination || {}),
					},
				},
			}
		},
		SUBSCRIPTION_LIST_STATUS(state, value) {
			state.subscriptionListStatus = value
		},
		SUBSCRIPTION_CREATE_STATUS(state, value) {
			state.subscriptionCreateStatus = value
		},
		SUBSCRIPTION_BULK_CREATE_STATUS(state, value) {
			state.subscriptionBulkCreateStatus = value
		},
		SUBSCRIPTION_UPGRADE_STATUS(state, value) {
			state.subscriptionUpgradeStatus = value
		},
		SUBSCRIPTION_CANCEL_STATUS(state, value) {
			state.subscriptionCancelStatus = value
		},
	},
	actions: {
		async fetchSubscriptionList({ commit }, params) {
			const key = cacheKey(params)
			const requestKey = ++subscriptionListRequestKey
			const cached = subscriptionListCache.get(key)

			if (cached) {
				commit('SUBSCRIPTION_LIST', cached)
				commit('SUBSCRIPTION_LIST_STATUS', 'init')
				return cached
			}

			commit('SUBSCRIPTION_LIST_STATUS', 'pending')
			try {
				let pendingRequest = subscriptionListPending.get(key)
				if (!pendingRequest) {
					pendingRequest = subscriptionList(params)
					subscriptionListPending.set(key, pendingRequest)
				}

				const { data } = await pendingRequest
				subscriptionListCache.set(key, data)
				subscriptionListPending.delete(key)
				if (requestKey !== subscriptionListRequestKey) return data

				commit('SUBSCRIPTION_LIST', data)
				commit('SUBSCRIPTION_LIST_STATUS', 'init')
				return data
			} catch (error) {
				subscriptionListPending.delete(key)
				throw error
			} finally {
				if (requestKey === subscriptionListRequestKey) {
					commit('SUBSCRIPTION_LIST_STATUS', 'init')
				}
			}
		},
		async createSubscription({ commit }, payload) {
			commit('SUBSCRIPTION_CREATE_STATUS', 'pending')
			try {
				const { data } = await subscriptionCreate(payload)
				clearSubscriptionCache()
				commit('SUBSCRIPTION_CREATE_STATUS', 'init')
				return data
			} catch (error) {
				commit('SUBSCRIPTION_CREATE_STATUS', 'init')
				throw error
			}
		},
		async bulkCreateSubscriptions({ commit }, payload) {
			commit('SUBSCRIPTION_BULK_CREATE_STATUS', 'pending')
			try {
				const { data } = await subscriptionBulkCreate(payload)
				clearSubscriptionCache()
				commit('SUBSCRIPTION_BULK_CREATE_STATUS', 'init')
				return data
			} catch (error) {
				commit('SUBSCRIPTION_BULK_CREATE_STATUS', 'init')
				throw error
			}
		},
		async upgradeSubscription({ commit }, { id, ...payload }) {
			commit('SUBSCRIPTION_UPGRADE_STATUS', 'pending')
			try {
				const { data } = await subscriptionUpgrade(id, payload)
				clearSubscriptionCache()
				commit('SUBSCRIPTION_UPGRADE_STATUS', 'init')
				return data
			} catch (error) {
				commit('SUBSCRIPTION_UPGRADE_STATUS', 'init')
				throw error
			}
		},
		async cancelSubscription({ commit }, { id, reason }) {
			commit('SUBSCRIPTION_CANCEL_STATUS', 'pending')
			try {
				const { data } = await subscriptionCancel(id, { reason })
				clearSubscriptionCache()
				commit('SUBSCRIPTION_CANCEL_STATUS', 'init')
				return data
			} catch (error) {
				commit('SUBSCRIPTION_CANCEL_STATUS', 'init')
				throw error
			}
		},
	},
}
